// Demo credit notes (avoirs) for purchase invoices.
// Mix of FINANCIER (cash refunds) and PRODUITS (returned goods) credits
// derived from existing achats and fournisseurs.

import { newId, nowIso, dateOffsetYmd } from "../uuid.js";

export function buildAvoirs(achats = [], fournisseurs = []) {
  const fournisseurById = new Map((fournisseurs || []).map((f) => [f.id, f]));
  const achatList = (achats || []).filter((a) => a && a.fournisseur_id);

  const pickAchat = (index) => achatList[index % Math.max(achatList.length, 1)] || null;

  const refFournisseur = (achat, fallbackName) => {
    if (!achat) return { id: null, nom: fallbackName };
    const f = fournisseurById.get(achat.fournisseur_id);
    return { id: achat.fournisseur_id, nom: f?.nom || fallbackName };
  };

  const refAchat = (achat) => {
    if (!achat) return { id: null, numero_interne: null, fournisseur_id: null };
    return { id: achat.id, numero_interne: achat.numero_interne || null, fournisseur_id: achat.fournisseur_id };
  };

  const records = [];

  // FINANCIER #1 — PAYE
  {
    const achat = pickAchat(0);
    const fournisseur = refFournisseur(achat, "Saidal Distribution");
    const montant_ht = 12500;
    const montant_tva = Math.round(montant_ht * 0.19);
    const montant_ttc = montant_ht + montant_tva;
    records.push({
      id: newId("avoir"),
      numero_interne: "AV-2026-001",
      type_avoir: "FINANCIER",
      achat_id: achat?.id || null,
      achat: refAchat(achat),
      fournisseur_id: fournisseur.id,
      fournisseur,
      date_avoir: dateOffsetYmd(-18),
      montant_ht,
      montant_tva,
      montant_ttc,
      statut_paiement: "PAYE",
      date_paiement: dateOffsetYmd(-10),
      received_amount: montant_ttc,
      notes_paiement: "Virement bancaire BNA - reference VIR-2026-0118",
      motif: "Geste commercial",
      details: null,
      created_at: nowIso(),
      updated_at: nowIso(),
    });
  }

  // FINANCIER #2 — NON_RECU
  {
    const achat = pickAchat(1);
    const fournisseur = refFournisseur(achat, "Biopharm");
    const montant_ht = 8750;
    const montant_tva = Math.round(montant_ht * 0.19);
    const montant_ttc = montant_ht + montant_tva;
    records.push({
      id: newId("avoir"),
      numero_interne: "AV-2026-002",
      type_avoir: "FINANCIER",
      achat_id: achat?.id || null,
      achat: refAchat(achat),
      fournisseur_id: fournisseur.id,
      fournisseur,
      date_avoir: dateOffsetYmd(-7),
      montant_ht,
      montant_tva,
      montant_ttc,
      statut_paiement: "NON_RECU",
      date_paiement: null,
      received_amount: null,
      notes_paiement: "En attente de virement fournisseur",
      motif: "Erreur facturation",
      details: null,
      created_at: nowIso(),
      updated_at: nowIso(),
    });
  }

  // PRODUITS #1 — RECU (1 line)
  {
    const achat = pickAchat(2);
    const fournisseur = refFournisseur(achat, "El Kendi");
    const ligne_ht = 4200;
    const montant_ht = ligne_ht;
    const montant_tva = Math.round(montant_ht * 0.19);
    const montant_ttc = montant_ht + montant_tva;
    records.push({
      id: newId("avoir"),
      numero_interne: "AV-2026-003",
      type_avoir: "PRODUITS",
      achat_id: achat?.id || null,
      achat: refAchat(achat),
      fournisseur_id: fournisseur.id,
      fournisseur,
      date_avoir: dateOffsetYmd(-14),
      montant_ht,
      montant_tva,
      montant_ttc,
      statut_paiement: "RECU",
      date_paiement: dateOffsetYmd(-12),
      received_amount: 0,
      notes_paiement: "Avoir comptabilise - compensation sur prochaine commande",
      motif: "Produits endommagés",
      details: [
        { id: newId("avd"), produit_id: "prod-doliprane-500", produit: { nom_commercial: "Doliprane 500mg", ppa: 180 }, numero_lot: "LOT-DOL-2025-A47", quantite: 30, prix_unitaire_ht: 140, montant_ligne_ht: 4200 },
      ],
      created_at: nowIso(),
      updated_at: nowIso(),
    });
  }

  // PRODUITS #2 — NON_RECU (2 lines)
  {
    const achat = pickAchat(3);
    const fournisseur = refFournisseur(achat, "Merinal");
    const l1 = 2800; const l2 = 3600;
    const montant_ht = l1 + l2;
    const montant_tva = Math.round(montant_ht * 0.19);
    const montant_ttc = montant_ht + montant_tva;
    records.push({
      id: newId("avoir"),
      numero_interne: "AV-2026-004",
      type_avoir: "PRODUITS",
      achat_id: achat?.id || null,
      achat: refAchat(achat),
      fournisseur_id: fournisseur.id,
      fournisseur,
      date_avoir: dateOffsetYmd(-4),
      montant_ht,
      montant_tva,
      montant_ttc,
      statut_paiement: "NON_RECU",
      date_paiement: null,
      received_amount: null,
      notes_paiement: "En attente de validation par le fournisseur",
      motif: "Erreur facturation",
      details: [
        { id: newId("avd"), produit_id: "prod-amoxicilline-1g", produit: { nom_commercial: "Amoxicilline 1g", ppa: 320 }, numero_lot: "LOT-AMX-2025-B12", quantite: 14, prix_unitaire_ht: 200, montant_ligne_ht: 2800 },
        { id: newId("avd"), produit_id: "prod-spasfon-80", produit: { nom_commercial: "Spasfon 80mg", ppa: 240 }, numero_lot: "LOT-SPF-2025-C08", quantite: 24, prix_unitaire_ht: 150, montant_ligne_ht: 3600 },
      ],
      created_at: nowIso(),
      updated_at: nowIso(),
    });
  }

  // PRODUITS #3 — RECU (3 lines)
  {
    const achat = pickAchat(4);
    const fournisseur = refFournisseur(achat, "Sanofi Algerie");
    const l1 = 1500; const l2 = 2250; const l3 = 1875;
    const montant_ht = l1 + l2 + l3;
    const montant_tva = Math.round(montant_ht * 0.19);
    const montant_ttc = montant_ht + montant_tva;
    records.push({
      id: newId("avoir"),
      numero_interne: "AV-2026-005",
      type_avoir: "PRODUITS",
      achat_id: achat?.id || null,
      achat: refAchat(achat),
      fournisseur_id: fournisseur.id,
      fournisseur,
      date_avoir: dateOffsetYmd(-21),
      montant_ht,
      montant_tva,
      montant_ttc,
      statut_paiement: "RECU",
      date_paiement: dateOffsetYmd(-19),
      received_amount: 0,
      notes_paiement: "Reception confirmee - stock reintegre en magasin principal",
      motif: "Geste commercial",
      details: [
        { id: newId("avd"), produit_id: "prod-efferalgan-1g", produit: { nom_commercial: "Efferalgan 1g", ppa: 250 }, numero_lot: "LOT-EFF-2025-D22", quantite: 10, prix_unitaire_ht: 150, montant_ligne_ht: 1500 },
        { id: newId("avd"), produit_id: "prod-smecta-3g", produit: { nom_commercial: "Smecta 3g", ppa: 300 }, numero_lot: "LOT-SMC-2025-E15", quantite: 15, prix_unitaire_ht: 150, montant_ligne_ht: 2250 },
        { id: newId("avd"), produit_id: "prod-maalox-250", produit: { nom_commercial: "Maalox 250ml", ppa: 375 }, numero_lot: "LOT-MAA-2025-F03", quantite: 5, prix_unitaire_ht: 375, montant_ligne_ht: 1875 },
      ],
      created_at: nowIso(),
      updated_at: nowIso(),
    });
  }

  return records;
}
